import { RefObject, useEffect, useRef } from 'react';

/**
 * O botão que está esperando o servidor se enche de cor (26/10).
 *
 * ── Por que ──────────────────────────────────────────────────────────────
 *
 * "Ok, entendi" chamava o servidor e o botão só ficava desabilitado: um
 * pouco mais apagado, e nada mais. Parecia que o toque não tinha pegado.
 *
 * ── Por que o botão se enche, e não rodinha nem "Lançando…" ─────────────
 *
 * Os dois mudam a largura do botão, e no celular os botões dividem a linha:
 * "Lançar" virando "Lançando…" empurra o vizinho para baixo. O preenchimento
 * fica atrás do texto (`index.css`, `button[aria-busy]`), e o botão não muda
 * de tamanho.
 *
 * ── Por que marcar pelo toque, e não botão a botão ───────────────────────
 *
 * A tela guarda o que está ocupado pelo ITEM: "Apagar lançamento" e "Manter"
 * dividem a mesma chave, e não daria para saber qual dos dois se enche. Em vez de
 * reescrever as chaves das dezenas de botões, este hook lembra qual botão foi
 * tocado e o marca enquanto `ocupado` durar. Botão novo na tela ganha o preenchimento
 * sem ninguém lembrar dele.
 *
 * O toque só vale se for recente: uma ação que começa sozinha (a volta da
 * conexão do banco, por exemplo) não enche um botão tocado minutos
 * antes.
 */
const TOQUE_RECENTE_MS = 1000;
/** O mesmo tempo de `botao-completa` no `index.css`, com folga. */
const DURACAO_DO_FIM_MS = 400;

export function useBotaoOcupado(container: RefObject<HTMLElement | null>, ocupado: boolean) {
  const toque = useRef<{ botao: HTMLButtonElement; quando: number } | null>(null);

  useEffect(() => {
    /*
     * Ouve no documento e confere o container na hora do toque: a tela que
     * abre com "Carregando…" só monta o container depois, e um ouvinte preso
     * nele na montagem nunca ouviria nada (Nossa Conta, 27/10).
     */
    const lembrar = (e: Event) => {
      const botao = (e.target as Element | null)?.closest?.('button');
      if (botao && container.current?.contains(botao)) toque.current = { botao, quando: Date.now() };
    };
    // Captura: o clique chega aqui antes do onClick do botão disparar a ação.
    document.addEventListener('click', lembrar, true);
    return () => document.removeEventListener('click', lembrar, true);
  }, [container]);

  useEffect(() => {
    if (!ocupado) return;
    const t = toque.current;
    if (!t || Date.now() - t.quando > TOQUE_RECENTE_MS || !t.botao.isConnected) return;
    const { botao } = t;
    botao.removeAttribute('data-botao-completo');
    botao.setAttribute('aria-busy', 'true');
    return () => {
      /*
       * A resposta chegou: o preenchimento completa a partir de onde parou,
       * e não de 90%. Uma chamada rápida fica em 30%, e pular dali para 90%
       * antes de completar seria um tranco.
       */
      const m = getComputedStyle(botao, '::after').transform.match(/^matrix\(([^,]+)/);
      botao.style.setProperty('--barra-de', m ? m[1] : '0.9');
      botao.removeAttribute('aria-busy');
      botao.setAttribute('data-botao-completo', '');
      setTimeout(() => {
        botao.removeAttribute('data-botao-completo');
        botao.style.removeProperty('--barra-de');
      }, DURACAO_DO_FIM_MS);
    };
  }, [ocupado]);
}
